import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'

// Lazy-load the markdown renderer + sanitizer so the public booking pages
// don't pay the bundle cost until a policy is actually displayed.
const ReactMarkdown = lazy(() => import('react-markdown'))

let cachedSanitizeSchema = null
let cachedRemarkGfm = null

const loadPlugins = async () => {
  if (cachedSanitizeSchema && cachedRemarkGfm)
    return { sanitize: cachedSanitizeSchema, gfm: cachedRemarkGfm }
  const [sanitize, gfm] = await Promise.all([import('rehype-sanitize'), import('remark-gfm')])
  cachedSanitizeSchema = sanitize
  cachedRemarkGfm = gfm
  return { sanitize, gfm }
}

const Renderer = ({ content, proseSize }) => {
  const [rehypePlugins, setRehypePlugins] = React.useState(null)
  const [remarkPlugins, setRemarkPlugins] = React.useState(null)

  React.useEffect(() => {
    let cancelled = false
    loadPlugins().then(({ sanitize, gfm }) => {
      if (cancelled) return
      // defaultSchema strips <script>, on* handlers, javascript: URLs, etc.
      setRehypePlugins([[sanitize.default, sanitize.defaultSchema]])
      setRemarkPlugins([gfm.default])
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!rehypePlugins || !remarkPlugins) {
    return <p className="text-sm text-gray-400">Loading policy…</p>
  }

  return (
    <div
      className={`prose ${proseSize} max-w-none prose-table:text-sm prose-th:bg-gray-100 prose-td:align-top`}
    >
      <ReactMarkdown rehypePlugins={rehypePlugins} remarkPlugins={remarkPlugins}>
        {content}
      </ReactMarkdown>
    </div>
  )
}

Renderer.propTypes = {
  content: PropTypes.string.isRequired,
  proseSize: PropTypes.string.isRequired,
}

/**
 * Public-facing display panel for an active cancellation/terms policy version.
 *
 * Props:
 *   policy: { id, version, content, created_at } | null
 *   title:  string heading (e.g. "Cancellation Policy")
 *   defaultOpen: bool (default false — collapsed)
 */
const PolicyDisplay = ({ policy, title, defaultOpen = false, proseSize = 'prose-sm' }) => {
  const [open, setOpen] = React.useState(defaultOpen)

  if (!policy) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        No {title.toLowerCase()} has been published yet.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="font-medium text-gray-900">{title}</span>
        <span className="text-xs text-gray-500">
          v{policy.version} {open ? '▲' : '▼'}
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-200 px-4 py-3">
          <Suspense fallback={<p className="text-sm text-gray-400">Loading policy…</p>}>
            <Renderer content={policy.content || ''} proseSize={proseSize} />
          </Suspense>
        </div>
      )}
    </div>
  )
}

PolicyDisplay.propTypes = {
  proseSize: PropTypes.string,
  policy: PropTypes.shape({
    id: PropTypes.number,
    version: PropTypes.string,
    content: PropTypes.string,
    created_at: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
}

PolicyDisplay.defaultProps = {
  policy: null,
  defaultOpen: false,
  proseSize: 'prose-sm',
}

export default PolicyDisplay
